<template>
  <div class="ExternalProjectDetail">
    <div class="header">
      <router-link
        :to="{ name: 'externalProjectsList' }"
        class="ExternalProjectDetail__back"
      >
        &larr; External Projects
      </router-link>
      <div class="header-title">Project Detail</div>
    </div>

    <div v-if="loading" class="ExternalProjectDetail__loading">Loading...</div>

    <div v-else-if="fetchError" class="ExternalProjectDetail__error">
      <span>{{ fetchError }}</span>
      <button class="button button-primary" @click="load">Retry</button>
    </div>

    <template v-else-if="currentProject">
      <div class="panel">
        <div class="ExternalProjectDetail__meta">
          <div class="ExternalProjectDetail__meta-row">
            <span class="ExternalProjectDetail__meta-label">Name</span>
            <span>{{ currentProject.name }}</span>
          </div>
          <div class="ExternalProjectDetail__meta-row">
            <span class="ExternalProjectDetail__meta-label">Owner</span>
            <a
              :href="`${kcBaseUrl}/user/${currentProject.owner.username}`"
              target="_blank"
              rel="noopener noreferrer"
              class="ExternalProjectDetail__owner-link"
              >{{ currentProject.owner.username }}</a
            >
          </div>
          <div class="ExternalProjectDetail__meta-row">
            <span class="ExternalProjectDetail__meta-label">Scopes</span>
            <span>
              <span
                v-for="scope in currentProject.scopes"
                :key="scope"
                class="ExternalProjectDetail__scope-tag"
                >{{ scope }}</span
              >
            </span>
          </div>
          <div class="ExternalProjectDetail__meta-row">
            <span class="ExternalProjectDetail__meta-label">Submitted At</span>
            <span>{{
              currentProject.inserted_at | moment('MMM DD, YYYY HH:mm')
            }}</span>
          </div>
          <div class="ExternalProjectDetail__meta-row">
            <span class="ExternalProjectDetail__meta-label">Status</span>
            <status-badge :status="currentProject.status" />
          </div>
          <div v-if="currentProject.description" class="ExternalProjectDetail__meta-row">
            <span class="ExternalProjectDetail__meta-label">Description</span>
            <span>{{ currentProject.description }}</span>
          </div>
          <div v-if="currentProject.contact_person_email" class="ExternalProjectDetail__meta-row">
            <span class="ExternalProjectDetail__meta-label">Contact Email</span>
            <span>{{ currentProject.contact_person_email }}</span>
          </div>
          <div v-if="currentProject.contact_person_kc_username" class="ExternalProjectDetail__meta-row">
            <span class="ExternalProjectDetail__meta-label">Contact KC Username</span>
            <a
              :href="`${kcBaseUrl}/user/${currentProject.contact_person_kc_username}`"
              target="_blank"
              rel="noopener noreferrer"
              class="ExternalProjectDetail__owner-link"
              >{{ currentProject.contact_person_kc_username }}</a
            >
          </div>
        </div>

        <div class="ExternalProjectDetail__actions">
          <button
            v-if="currentProject.status !== 'accepted'"
            class="button button-success"
            @click="openModal('accept')"
          >
            Accept
          </button>
          <button
            v-if="currentProject.status !== 'rejected'"
            class="button button-danger"
            @click="openModal('reject')"
          >
            Reject
          </button>
        </div>
      </div>

      <div class="panel">
        <div class="ExternalProjectDetail__section-title">
          Applications ({{ applications.length }})
        </div>

        <div v-if="appsLoading" class="ExternalProjectDetail__loading">
          Loading applications...
        </div>

        <div v-else-if="appsError" class="ExternalProjectDetail__error">
          <span>{{ appsError }}</span>
          <button class="button button-primary" @click="loadApplications">
            Retry
          </button>
        </div>

        <table v-else class="ExternalProjectDetail__table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Status</th>
            </tr>
          </thead>
          <tr v-if="applications.length === 0">
            <td colspan="3" class="ExternalProjectDetail__empty">
              No applications yet.
            </td>
          </tr>
          <tr
            v-for="app in applications"
            :key="app.id"
            class="ExternalProjectDetail__row"
            @click="viewApplication(app.id)"
          >
            <td>{{ app.name }}</td>
            <td>{{ app.type }}</td>
            <td><status-badge :status="app.status" /></td>
          </tr>
        </table>
      </div>
    </template>

    <action-confirm-modal
      v-if="modal.visible"
      :title="`${modal.action === 'accept' ? 'Accept' : 'Reject'} Project?`"
      :content="
        modal.action === 'reject'
          ? `Are you sure you want to reject &quot;${currentProject.name}&quot;? The project will lose access to the API and all its applications will be rejected as well.`
          : `Are you sure you want to accept &quot;${currentProject.name}&quot;?`
      "
      :loading="modal.loading"
      :error-message="modal.error"
      :require-delete-confirmation="modal.action === 'reject'"
      @confirm="confirmAction"
      @cancel="closeModal"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import StatusBadge from './StatusBadge.vue';
import ActionConfirmModal from './ActionConfirmModal.vue';
import { REMOTE_PATHS } from '../../constants/remote-paths.constant';

const { mapGetters, mapActions, mapMutations } =
  createNamespacedHelpers('externalProjects');

export default {
  name: 'ExternalProjectDetail',
  components: { StatusBadge, ActionConfirmModal },
  data() {
    return {
      loading: false,
      fetchError: null,
      appsLoading: false,
      appsError: null,
      modal: {
        visible: false,
        action: null,
        loading: false,
        error: null,
      },
    };
  },
  computed: {
    ...mapGetters(['currentProject', 'applications']),
    kcBaseUrl() {
      return REMOTE_PATHS.API_PATH.includes('kingsch.at')
        ? 'https://kingschat.online'
        : 'https://stg.kingschat.online';
    },
  },
  created() {
    this.load();
  },
  beforeDestroy() {
    this.clearCurrentProject();
    this.clearApplications();
  },
  methods: {
    ...mapActions([
      'fetchProject',
      'fetchApplications',
      'acceptProject',
      'rejectProject',
    ]),
    ...mapMutations(['clearCurrentProject', 'clearApplications']),
    async load() {
      this.loading = true;
      this.fetchError = null;
      const { error } = await this.fetchProject(this.$route.params.id);
      this.loading = false;
      if (error) {
        if (error.status === 404) {
          this.$router.push({ name: 'externalProjectsList' });
          return;
        }
        this.fetchError = this.getErrorMessage(error);
        return;
      }
      this.loadApplications();
    },
    async loadApplications() {
      this.appsLoading = true;
      this.appsError = null;
      const { error } = await this.fetchApplications(this.$route.params.id);
      this.appsLoading = false;
      if (error) {
        this.appsError = this.getErrorMessage(error);
      }
    },
    openModal(action) {
      this.modal = { visible: true, action, loading: false, error: null };
    },
    closeModal() {
      this.modal = {
        visible: false,
        action: null,
        loading: false,
        error: null,
      };
    },
    async confirmAction() {
      this.modal.loading = true;
      this.modal.error = null;
      const action =
        this.modal.action === 'accept'
          ? this.acceptProject
          : this.rejectProject;
      const { error } = await action(this.$route.params.id);
      this.modal.loading = false;
      if (error) {
        this.modal.error = this.getErrorMessage(error);
        return;
      }
      const wasReject = this.modal.action === 'reject';
      this.closeModal();
      if (wasReject) {
        this.loadApplications();
      }
    },
    viewApplication(id) {
      this.$router.push({
        name: 'applicationDetail',
        params: { projectId: this.$route.params.id, id },
      });
    },
    getErrorMessage(error) {
      const messages = {
        401: 'Your session has expired. Please log in again.',
        403: "You don't have permission to perform this action.",
        404: 'This item could not be found.',
        422: 'This action is no longer available for this item.',
        500: 'Something went wrong. Please try again.',
      };
      return (
        messages[error && error.status] ||
        'Something went wrong. Please try again.'
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.ExternalProjectDetail {
  &__back {
    display: inline-block;
    margin-bottom: 8px;
    color: #337ab7;
    font-size: 13px;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__breadcrumb {
    font-size: 13px;
    color: #666;
    margin-bottom: 20px;

    a {
      color: #337ab7;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__meta {
    margin-bottom: 24px;
  }

  &__meta-row {
    display: flex;
    padding: 10px 0;
    border-bottom: 1px solid #f0f0f0;
    font-size: 14px;
  }

  &__meta-label {
    width: 160px;
    font-weight: 600;
    color: #555;
    flex-shrink: 0;
  }

  &__owner-link {
    color: #337ab7;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__scope-tag {
    display: inline-block;
    background: #e9ecef;
    color: #495057;
    padding: 2px 8px;
    border-radius: 3px;
    font-size: 12px;
    margin-right: 4px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;

    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__section-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 16px;
    color: #333;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
      padding: 12px 16px;
      text-align: left;
      border-bottom: 1px solid #eee;
    }

    th {
      font-weight: 600;
      color: #666;
      font-size: 13px;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
  }

  &__row {
    cursor: pointer;

    &:hover {
      background-color: #f8f9fa;
    }
  }

  &__empty {
    text-align: center;
    padding: 24px;
    color: #999;
    font-size: 14px;
  }

  &__loading {
    padding: 24px;
    color: #666;
    font-size: 14px;
  }

  &__error {
    padding: 14px 16px;
    background: #f8d7da;
    color: #721c24;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 16px;
  }
}
</style>
